import { getUser, getToken } from "../utils/authService";
import { useState, useEffect } from "react";
import { DollarSign, Calendar, Users, CheckCircle, Clock, FileText, Download, RefreshCw, Edit, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../utils/apiClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const PayrollManagement = () => {
  const [payroll, setPayroll] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [stats, setStats] = useState(null);
  
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [genMonth, setGenMonth] = useState(new Date().getMonth() + 1);
  const [genYear, setGenYear] = useState(new Date().getFullYear());
  
  // Manual Deduction State
  const [deductionDialogOpen, setDeductionDialogOpen] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [deductionForm, setDeductionForm] = useState({
    pf_deduction: 0,
    esi_deduction: 0,
    tds_deduction: 0,
    advance_deduction: 0,
    loan_deduction: 0,
    penalty_deduction: 0,
    other_deductions: 0,
    deduction_remarks: "",
    allowances: 0,
    bonus: 0,
    incentives: 0
  });

  const currentUser = getUser() || {};
  const canApprove = currentUser.role === "Master Admin" || currentUser.role === "Management";

  const months = ["January", "February", "March", "April", "May", "June", 
                  "July", "August", "September", "October", "November", "December"];

  useEffect(() => {
    fetchData();
  }, [selectedMonth, selectedYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [payrollRes, empRes, statsRes] = await Promise.all([
        apiClient.get(`/payroll?month=${selectedMonth}&year=${selectedYear}`),
        apiClient.get('/employees/active'),
        apiClient.get(`/payroll/stats/${selectedMonth}/${selectedYear}`)
      ]);
      setPayroll(payrollRes.data);
      setEmployees(empRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const generatePayroll = async () => {
    try {
      const response = await apiClient.post('/payroll/generate', {
        month: genMonth,
        year: genYear
      });
      toast.success(response.data.message);
      setGenerateDialogOpen(false);
      setSelectedMonth(genMonth);
      setSelectedYear(genYear);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate payroll");
    }
  };

  const approvePayroll = async (payrollId) => {
    try {
      await apiClient.put(`/payroll/${payrollId}/approve`);
      toast.success("Payroll approved");
      fetchData();
    } catch (error) {
      toast.error("Failed to approve");
    }
  };

  const markPaid = async (payrollId) => {
    const mode = prompt("Enter payment mode (Bank Transfer/Cash/Cheque):", "Bank Transfer");
    if (!mode) return;
    const ref = prompt("Enter payment reference/transaction number:", "");
    
    try {
      await apiClient.put(`/payroll/${payrollId}/pay?payment_mode=${encodeURIComponent(mode)}&payment_reference=${encodeURIComponent(ref || "")}`);
      toast.success("Marked as paid");
      fetchData();
    } catch (error) {
      toast.error("Failed to mark as paid");
    }
  };

  // Open deduction dialog with current values
  const openDeductionDialog = (p) => {
    setSelectedPayroll(p);
    setDeductionForm({
      pf_deduction: p.pf_deduction || 0,
      esi_deduction: p.esi_deduction || 0,
      tds_deduction: p.tds_deduction || 0,
      advance_deduction: p.advance_deduction || 0,
      loan_deduction: p.loan_deduction || 0,
      penalty_deduction: p.penalty_deduction || 0,
      other_deductions: p.other_deductions || 0,
      deduction_remarks: p.deduction_remarks || "",
      allowances: p.allowances || 0,
      bonus: p.bonus || 0,
      incentives: p.incentives || 0
    });
    setDeductionDialogOpen(true);
  };

  const handleDeductionSubmit = async () => {
    if (!selectedPayroll) return;
    try {
      const response = await apiClient.put(`/payroll/${selectedPayroll.id}/deductions`, deductionForm);
      toast.success(`Deductions updated. New Net: ₹${response.data.net_salary?.toLocaleString()}`);
      setDeductionDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update deductions");
    }
  };

  const downloadPayslip = async (payrollId) => {
    try {
      const token = getToken();
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/payslip/${payrollId}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `payslip_${payrollId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success("Payslip downloaded");
    } catch (error) {
      toast.error("Failed to download payslip");
    }
  };

  const downloadSummary = async () => {
    try {
      const token = getToken();
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/payroll-summary/${selectedMonth}/${selectedYear}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `payroll_summary_${selectedMonth}_${selectedYear}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success("Summary downloaded");
    } catch (error) {
      toast.error("Failed to download summary");
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      "Draft": { color: "bg-slate-200 text-slate-600", icon: Clock },
      "Approved": { color: "bg-accent/20 text-accent", icon: CheckCircle },
      "Paid": { color: "bg-success/20 text-success", icon: CheckCircle }
    };
    const { color, icon: Icon } = config[status] || config["Draft"];
    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-sm text-xs font-medium ${color}`}>
        <Icon className="w-3 h-3" />
        <span>{status}</span>
      </span>
    );
  };

  if (loading) return <div className="text-center py-12">Loading...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading font-bold text-4xl text-primary tracking-tight">PAYROLL MANAGEMENT</h1>
          <p className="text-slate-600 mt-2 font-body">Generate and manage employee payroll</p>
        </div>
        <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
          <DialogTrigger asChild>
            <button className="btn-primary flex items-center space-x-2">
              <RefreshCw className="w-4 h-4" />
              <span>Generate Payroll</span>
            </button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">GENERATE PAYROLL</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-sm text-slate-600">
                Generate payroll for all active employees based on their attendance records.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Month</label>
                  <select
                    value={genMonth}
                    onChange={(e) => setGenMonth(parseInt(e.target.value))}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {months.map((m, idx) => (
                      <option key={idx} value={idx + 1}>{m}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Year</label>
                  <select
                    value={genYear}
                    onChange={(e) => setGenYear(parseInt(e.target.value))}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {[2024, 2025, 2026].map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="bg-slate-50 p-3 rounded-sm text-sm">
                <p><strong>Active Employees:</strong> {employees.length}</p>
                <p className="text-slate-500 text-xs mt-1">Payroll will be calculated from attendance data for the selected month.</p>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button onClick={() => setGenerateDialogOpen(false)} className="btn-outline">Cancel</button>
                <button onClick={generatePayroll} className="btn-primary">Generate</button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Month/Year Selector */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Calendar className="w-5 h-5 text-slate-400" />
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {months.map((m, idx) => (
                <option key={idx} value={idx + 1}>{m}</option>
              ))}
            </select>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {[2024, 2025, 2026].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
        {payroll.length > 0 && (
          <button onClick={downloadSummary} className="btn-outline flex items-center space-x-2">
            <Download className="w-4 h-4" />
            <span>Download Summary</span>
          </button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-4 gap-4 mb-8">
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Total Employees</p>
          <p className="font-heading font-black text-3xl text-primary font-mono">{stats?.total_employees || 0}</p>
        </div>
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Gross Salary</p>
          <p className="font-heading font-black text-2xl text-primary font-mono">₹{(stats?.total_gross || 0).toLocaleString()}</p>
        </div>
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Total Deductions</p>
          <p className="font-heading font-black text-2xl text-error font-mono">₹{(stats?.total_deductions || 0).toLocaleString()}</p>
        </div>
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Net Salary</p>
          <p className="font-heading font-black text-2xl text-success font-mono">₹{(stats?.total_net || 0).toLocaleString()}</p>
        </div>
      </div>

      {/* Status Summary */}
      {stats && (
        <div className="flex space-x-4 mb-6">
          <div className="flex items-center space-x-2">
            <span className="w-3 h-3 rounded-full bg-slate-300"></span>
            <span className="text-sm">Draft: {stats.draft_count}</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="w-3 h-3 rounded-full bg-accent"></span>
            <span className="text-sm">Approved: {stats.approved_count}</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="w-3 h-3 rounded-full bg-success"></span>
            <span className="text-sm">Paid: {stats.paid_count}</span>
          </div>
        </div>
      )}

      {/* Payroll Table */}
      <div className="stat-card overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b-2 border-slate-300">
              <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Code</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Employee</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Department</th>
              <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Present</th>
              <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">OT Hrs</th>
              <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Gross</th>
              <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Deductions</th>
              <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Net</th>
              <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Status</th>
              <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {payroll.map(p => (
              <tr key={p.id} className="border-b border-slate-200 hover:bg-slate-50">
                <td className="py-3 px-4 font-mono text-sm text-primary">{p.employee_code}</td>
                <td className="py-3 px-4 text-sm font-medium">{p.employee_name}</td>
                <td className="py-3 px-4 text-sm">{p.department}</td>
                <td className="py-3 px-4 text-center font-mono text-sm">{p.days_present}/{p.total_days}</td>
                <td className="py-3 px-4 text-center font-mono text-sm">{p.overtime_hours}</td>
                <td className="py-3 px-4 text-right font-mono text-sm">₹{p.gross_salary?.toLocaleString()}</td>
                <td className="py-3 px-4 text-right font-mono text-sm text-error">₹{p.total_deductions?.toLocaleString()}</td>
                <td className="py-3 px-4 text-right font-mono text-sm font-semibold text-success">₹{p.net_salary?.toLocaleString()}</td>
                <td className="py-3 px-4 text-center">{getStatusBadge(p.status)}</td>
                <td className="py-3 px-4 text-center">
                  <div className="flex items-center justify-center space-x-2">
                    {/* Edit Deductions - only for Draft and Approved */}
                    {p.status !== "Paid" && (
                      <button 
                        onClick={() => openDeductionDialog(p)} 
                        className="p-1 hover:bg-slate-200 rounded" 
                        title="Edit Deductions"
                      >
                        <Edit className="w-4 h-4 text-primary" />
                      </button>
                    )}
                    {canApprove && p.status === "Draft" && (
                      <button onClick={() => approvePayroll(p.id)} className="text-xs text-accent hover:underline">Approve</button>
                    )}
                    {canApprove && p.status === "Approved" && (
                      <button onClick={() => markPaid(p.id)} className="text-xs text-success hover:underline">Pay</button>
                    )}
                    <button onClick={() => downloadPayslip(p.id)} className="p-1 hover:bg-slate-200 rounded" title="Download Payslip">
                      <FileText className="w-4 h-4 text-slate-600" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {payroll.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <DollarSign className="w-16 h-16 mx-auto mb-4 text-slate-400" />
            <h3 className="font-heading font-bold text-xl mb-2">No Payroll Data</h3>
            <p className="text-slate-600 mb-4">
              No payroll generated for {months[selectedMonth - 1]} {selectedYear}.
            </p>
            <button onClick={() => setGenerateDialogOpen(true)} className="btn-primary">
              Generate Payroll
            </button>
          </div>
        )}
      </div>

      {/* Manual Deduction/Adjustment Dialog */}
      <Dialog open={deductionDialogOpen} onOpenChange={setDeductionDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              ADJUST SALARY - {selectedPayroll?.employee_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Employee Info */}
            <div className="bg-slate-50 p-4 rounded-sm">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div><span className="text-slate-500">Code:</span> {selectedPayroll?.employee_code}</div>
                <div><span className="text-slate-500">Department:</span> {selectedPayroll?.department}</div>
                <div><span className="text-slate-500">Basic:</span> ₹{selectedPayroll?.basic_salary?.toLocaleString()}</div>
              </div>
            </div>

            {/* Additions */}
            <div>
              <h3 className="font-heading font-bold text-sm uppercase tracking-widest text-success mb-3 flex items-center">
                <Plus className="w-4 h-4 mr-2" /> Additions
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Allowances</label>
                  <input
                    type="number"
                    value={deductionForm.allowances}
                    onChange={(e) => setDeductionForm({...deductionForm, allowances: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Bonus</label>
                  <input
                    type="number"
                    value={deductionForm.bonus}
                    onChange={(e) => setDeductionForm({...deductionForm, bonus: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Incentives</label>
                  <input
                    type="number"
                    value={deductionForm.incentives}
                    onChange={(e) => setDeductionForm({...deductionForm, incentives: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Deductions */}
            <div>
              <h3 className="font-heading font-bold text-sm uppercase tracking-widest text-error mb-3 flex items-center">
                <Minus className="w-4 h-4 mr-2" /> Deductions
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">PF Deduction</label>
                  <input
                    type="number"
                    value={deductionForm.pf_deduction}
                    onChange={(e) => setDeductionForm({...deductionForm, pf_deduction: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">ESI Deduction</label>
                  <input
                    type="number"
                    value={deductionForm.esi_deduction}
                    onChange={(e) => setDeductionForm({...deductionForm, esi_deduction: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">TDS Deduction</label>
                  <input
                    type="number"
                    value={deductionForm.tds_deduction}
                    onChange={(e) => setDeductionForm({...deductionForm, tds_deduction: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Advance Recovery</label>
                  <input
                    type="number"
                    value={deductionForm.advance_deduction}
                    onChange={(e) => setDeductionForm({...deductionForm, advance_deduction: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Loan EMI</label>
                  <input
                    type="number"
                    value={deductionForm.loan_deduction}
                    onChange={(e) => setDeductionForm({...deductionForm, loan_deduction: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Penalty</label>
                  <input
                    type="number"
                    value={deductionForm.penalty_deduction}
                    onChange={(e) => setDeductionForm({...deductionForm, penalty_deduction: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Other Deductions</label>
                  <input
                    type="number"
                    value={deductionForm.other_deductions}
                    onChange={(e) => setDeductionForm({...deductionForm, other_deductions: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-slate-700 mb-1">Remarks</label>
                  <input
                    type="text"
                    value={deductionForm.deduction_remarks}
                    onChange={(e) => setDeductionForm({...deductionForm, deduction_remarks: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                    placeholder="Reason for adjustments..."
                  />
                </div>
              </div>
            </div>

            {/* Summary Preview */}
            <div className="bg-primary/5 p-4 rounded-sm border border-primary/20">
              <h4 className="font-medium text-sm mb-2">Salary Preview</h4>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Total Additions:</span>
                  <span className="font-mono ml-2 text-success">
                    ₹{(deductionForm.allowances + deductionForm.bonus + deductionForm.incentives).toLocaleString()}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">Total Deductions:</span>
                  <span className="font-mono ml-2 text-error">
                    ₹{(deductionForm.pf_deduction + deductionForm.esi_deduction + deductionForm.tds_deduction + 
                       deductionForm.advance_deduction + deductionForm.loan_deduction + deductionForm.penalty_deduction + 
                       deductionForm.other_deductions).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button onClick={() => setDeductionDialogOpen(false)} className="btn-outline">Cancel</button>
              <button onClick={handleDeductionSubmit} className="btn-primary">Update Salary</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PayrollManagement;
